"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleDot,
  Gift,
  HandHeart,
  HardHat,
  HeartHandshake,
  ImageIcon,
  Landmark,
  Search,
  Ticket,
  Trophy,
  Users,
  Wrench,
  X,
} from "lucide-react";

type Sponsor = {
  name: string;
  level: string;
  logo?: string;
  url?: string;
};

type Supporter = {
  name: string;
};

type Contributor = {
  name: string;
  contribution: string;
  logo?: string;
  dark?: boolean;
};

type ContributorData = {
  fundraising: Contributor[];
  inKind: Contributor[];
};

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const stats = [
  { value: "500+", label: "Players served" },
  { value: "75", label: "Years of Smithfield Little League" },
  { value: "$120,000+", label: "Raised through sponsorships & fundraising" },
  { value: "2", label: "Major facility improvement locations" },
  { value: "54", label: "2026 sponsors" },
  { value: "38", label: "Community & fundraising supporters" },
];

const supportWays = [
  {
    icon: Trophy,
    title: "Sponsorships",
    text: "Local businesses and organizations investing directly in Smithfield Little League.",
  },
  {
    icon: Ticket,
    title: "Calendar raffle",
    text: "Players, families, friends and community members supporting a major league fundraising effort.",
  },
  {
    icon: CalendarDays,
    title: "Fundraising & events",
    text: "Community participation in league fundraising activities and events.",
  },
  {
    icon: Gift,
    title: "Donated items & prizes",
    text: "Businesses and individuals donating products, services, gift cards, baskets and raffle prizes.",
  },
  {
    icon: Wrench,
    title: "In-kind contributions",
    text: "Businesses, individuals and partners contributing labor, materials, expertise and services.",
  },
  {
    icon: HeartHandshake,
    title: "Community partnerships",
    text: "Town departments and community organizations helping make league improvements possible.",
  },
];

const volunteerGroups = [
  "Board of Directors",
  "Coaches",
  "Team parents",
  "Fundraising volunteers",
  "Opening Day & event volunteers",
  "Families",
  "Community volunteers",
];

const nextPriorities = [
  "Facilities that can serve the next generation",
  "Player-development spaces and resources",
  "A stronger Little League experience for every family",
];

function initials(name: string) {
  return name
    .replace(/\([^)]*\)/g, "")
    .split(/[\s/&-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");
}

function sponsorGroup(level: string) {
  if (level === "Grand Slam") return "grand-slam";
  if (level === "Home Run") return "home-run";
  if (level === "Team") return "team";
  if (level.includes("Scoreboard")) return "scoreboard";
  if (level === "Field") return "sign";
  return "digital";
}

function SectionIntro({
  eyebrow,
  title,
  children,
  light = false,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
  light?: boolean;
}) {
  return (
    <div className={`section-intro${light ? " section-intro--light" : ""}`}>
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children && <div className="section-copy">{children}</div>}
    </div>
  );
}

function BeforeAfter({
  fieldName,
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  beforeAlt,
  afterAlt,
}: {
  fieldName: string;
  beforeSrc?: string;
  afterSrc?: string;
  beforeLabel?: string;
  afterLabel?: string;
  beforeAlt?: string;
  afterAlt?: string;
}) {
  const [position, setPosition] = useState(50);

  if (!beforeSrc || !afterSrc) {
    return (
      <div className="comparison-placeholder" aria-label={`${fieldName} before and after photo placeholders`}>
        {[
          [beforeLabel, "Add the matched pre-improvement photograph"],
          [afterLabel, "Add the matched completed-project photograph"],
        ].map(([label, text]) => (
          <div className="photo-slot" key={label}>
            <span className="photo-label">{label}</span>
            <div className="photo-slot__content">
              <ImageIcon aria-hidden="true" />
              <strong>{fieldName}</strong>
              <p>{text}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="comparison" style={{ "--position": `${position}%` } as React.CSSProperties}>
      <img src={afterSrc} alt={afterAlt || `${fieldName} after improvements`} loading="lazy" />
      <div className="comparison__before">
        <img src={beforeSrc} alt={beforeAlt || `${fieldName} before improvements`} loading="lazy" />
      </div>
      <span className="comparison__label comparison__label--before">{beforeLabel}</span>
      <span className="comparison__label comparison__label--after">{afterLabel}</span>
      <div className="comparison__handle" aria-hidden="true" />
      <input
        type="range"
        min="0"
        max="100"
        value={position}
        onChange={(event) => setPosition(Number(event.target.value))}
        aria-label={`Compare before and after photographs of ${fieldName}`}
      />
    </div>
  );
}

function PhotoPair({
  leftSrc,
  rightSrc,
  leftLabel,
  rightLabel,
  leftAlt,
  rightAlt,
}: {
  leftSrc: string;
  rightSrc: string;
  leftLabel: string;
  rightLabel: string;
  leftAlt: string;
  rightAlt: string;
}) {
  return (
    <div className="photo-pair">
      <figure>
        <div className="photo-pair__media">
          <img src={leftSrc} alt={leftAlt} loading="lazy" />
          <span>{leftLabel}</span>
        </div>
      </figure>
      <figure>
        <div className="photo-pair__media">
          <img src={rightSrc} alt={rightAlt} loading="lazy" />
          <span>{rightLabel}</span>
        </div>
      </figure>
    </div>
  );
}

function FieldVideo({ src, poster, title, caption }: { src: string; poster: string; title: string; caption: string }) {
  return (
    <figure className="field-video">
      <video controls playsInline preload="metadata" poster={poster} aria-label={title}>
        <source src={src} type="video/mp4" />
        Your browser does not support embedded video.
      </video>
      <figcaption>
        <strong>{title}</strong>
        <span>{caption}</span>
      </figcaption>
    </figure>
  );
}

function SponsorMark({ sponsor, featured = false }: { sponsor: Sponsor; featured?: boolean }) {
  const content = (
    <>
      <div className={`sponsor-mark__logo${sponsor.logo ? " sponsor-mark__logo--image" : ""}`} aria-hidden={!sponsor.logo}>
        {sponsor.logo ? (
          <img src={sponsor.logo} alt={`${sponsor.name} logo`} loading="lazy" />
        ) : (
          <span>{initials(sponsor.name)}</span>
        )}
      </div>
      <div className="sponsor-mark__copy">
        <strong>{sponsor.name}</strong>
        <span>{sponsor.level}</span>
      </div>
      {sponsor.url && <ChevronRight className="sponsor-mark__arrow" aria-hidden="true" />}
    </>
  );

  const className = `sponsor-mark${featured ? " sponsor-mark--featured" : ""}`;

  return sponsor.url ? (
    <a className={className} href={sponsor.url} target="_blank" rel="noreferrer">
      {content}
      <span className="sr-only">Visit {sponsor.name}</span>
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}

function SponsorSection({ sponsors }: { sponsors: Sponsor[] }) {
  const groups = useMemo(() => {
    return sponsors.reduce<Record<string, Sponsor[]>>((acc, sponsor) => {
      const group = sponsorGroup(sponsor.level);
      (acc[group] ||= []).push(sponsor);
      return acc;
    }, {});
  }, [sponsors]);

  const groupDefinitions = [
    { key: "grand-slam", label: "Grand Slam Sponsor", note: "Our highest level of 2026 support" },
    { key: "home-run", label: "Home Run Sponsors", note: "Major league-wide partners" },
    { key: "team", label: "Team Sponsors", note: "Supporting teams across SLL divisions" },
    { key: "scoreboard", label: "Scoreboard Sponsors", note: "Visible partners at our fields" },
    { key: "sign", label: "Sign Sponsors", note: "Field-level community recognition" },
    { key: "digital", label: "Digital Sponsors", note: "Supporting SLL across digital channels" },
  ];

  return (
    <div className="sponsor-groups">
      {groupDefinitions.map((definition) => {
        const items = groups[definition.key] || [];
        if (!items.length) return null;
        return (
          <section className={`sponsor-tier sponsor-tier--${definition.key}`} key={definition.key}>
            <div className="sponsor-tier__heading">
              <div>
                <p>{definition.label}</p>
                <span>{definition.note}</span>
              </div>
              <span className="tier-count">{items.length}</span>
            </div>
            <div className="sponsor-grid">
              {items.map((sponsor) => (
                <SponsorMark
                  sponsor={sponsor}
                  featured={definition.key === "grand-slam"}
                  key={`${sponsor.name}-${sponsor.level}`}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SupporterDirectory({ supporters }: { supporters: Supporter[] }) {
  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState("");
  const deferredQuery = useDeferredValue(query);

  const filtered = useMemo(() => {
    const normalizedQuery = deferredQuery.trim().toLocaleLowerCase();
    return supporters
      .filter((supporter) => {
        const matchesQuery = !normalizedQuery || supporter.name.toLocaleLowerCase().includes(normalizedQuery);
        const matchesLetter = !letter || supporter.name.trim().toLocaleUpperCase().startsWith(letter);
        return matchesQuery && matchesLetter;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deferredQuery, letter, supporters]);

  const visible = filtered.slice(0, 500);
  const clearFilters = () => {
    setQuery("");
    setLetter("");
  };

  return (
    <div className="directory-shell">
      <form className="directory-search" role="search" onSubmit={(event) => event.preventDefault()}>
        <label htmlFor="supporter-search">Search our supporters</label>
        <div className="search-control">
          <Search aria-hidden="true" />
          <input
            id="supporter-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Enter a public recognition name"
            autoComplete="off"
          />
          {(query || letter) && (
            <button type="button" onClick={clearFilters} aria-label="Clear supporter search and letter filter">
              <X aria-hidden="true" />
            </button>
          )}
        </div>
      </form>

      <div className="alphabet" aria-label="Browse supporters alphabetically">
        <button
          type="button"
          className={!letter ? "is-active" : ""}
          onClick={() => setLetter("")}
          aria-pressed={!letter}
        >
          All
        </button>
        {LETTERS.map((item) => (
          <button
            type="button"
            className={letter === item ? "is-active" : ""}
            onClick={() => setLetter(item)}
            aria-pressed={letter === item}
            key={item}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="directory-results" aria-live="polite">
        {!supporters.length ? (
          <div className="directory-empty">
            <Users aria-hidden="true" />
            <div>
              <strong>Our public recognition list is being finalized.</strong>
              <p>
                The directory is ready for the complete 2026 list and can grow to thousands of names without changing
                this page.
              </p>
            </div>
          </div>
        ) : !filtered.length ? (
          <div className="directory-empty">
            <Search aria-hidden="true" />
            <div>
              <strong>No matching recognition name was found.</strong>
              <p>Try another spelling, choose a different letter or clear the filters.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="directory-meta">
              <p>
                {filtered.length.toLocaleString()} {filtered.length === 1 ? "supporter" : "supporters"}
              </p>
              {filtered.length > visible.length && <span>Showing the first {visible.length.toLocaleString()} matches</span>}
            </div>
            <ul className="supporter-list">
              {visible.map((supporter, index) => (
                <li key={`${supporter.name}-${index}`}>{supporter.name}</li>
              ))}
            </ul>
          </>
        )}
      </div>
      <p className="directory-note">Our supporter list continues to grow as we complete our 2026 records.</p>
    </div>
  );
}

export default function CommunityPage() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [contributors, setContributors] = useState<ContributorData>({ fundraising: [], inKind: [] });
  const [dataReady, setDataReady] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch("/data/sponsors.json").then((response) => response.json()),
      fetch("/data/supporters.json").then((response) => response.json()),
      fetch("/data/contributors.json").then((response) => response.json()),
    ])
      .then(([sponsorData, supporterData, contributorData]) => {
        if (!active) return;
        setSponsors(Array.isArray(sponsorData.sponsors) ? sponsorData.sponsors : []);
        setSupporters(
          Array.isArray(supporterData.supporters)
            ? supporterData.supporters
                .map((item: string | Supporter) => (typeof item === "string" ? { name: item } : item))
                .filter((item: Supporter) => item?.name)
            : [],
        );
        setContributors({
          fundraising: Array.isArray(contributorData.fundraising) ? contributorData.fundraising : [],
          inKind: Array.isArray(contributorData.inKind) ? contributorData.inKind : [],
        });
        setDataReady(true);
      })
      .catch(() => setDataReady(true));

    return () => {
      active = false;
    };
  }, []);

  return (
    <main>
      <a className="skip-link" href="#impact">
        Skip to the impact story
      </a>

      <header className="hero" id="top">
        <img
          className="hero__photo"
          src="/assets/impact/connell-home-after.webp"
          alt="Doris M. Connell Little League Field viewed from behind home plate after the 2026 improvements"
          fetchPriority="high"
        />
        <div className="hero__wash" />
        <nav className="site-nav" aria-label="Impact report navigation">
          <a className="site-nav__brand" href="#top" aria-label="Smithfield Little League community impact home">
            <img src="/assets/sll-logo.webp" alt="" />
            <span>
              Smithfield Little League
              <small>2026 Community Impact</small>
            </span>
          </a>
          <div className="site-nav__links">
            <a href="#impact">Impact</a>
            <a href="#fields">Fields</a>
            <a href="#sponsors">Sponsors</a>
            <a href="#community">Community</a>
            <a href="#thank-you">Thank you</a>
          </div>
        </nav>

        <div className="hero__content">
          <div className="hero__anniversary">
            <img src="/assets/sll-75th-logo.webp" alt="Smithfield Little League 75th anniversary, 1951 to 2026" />
          </div>
          <div className="hero__words">
            <p className="eyebrow eyebrow--gold">Smithfield Little League · 75th Anniversary Season</p>
            <h1>What our community made possible.</h1>
            <p className="hero__lede">
              In 2026, businesses, families, volunteers and community partners came together to invest in our players,
              our facilities and the future of Smithfield Little League.
            </p>
            <div className="hero__actions">
              <a className="button button--gold" href="#impact">
                See the impact <ArrowDown aria-hidden="true" />
              </a>
              <span>Thank you for being part of it.</span>
            </div>
          </div>
        </div>
        <p className="hero__caption">Doris M. Connell Little League Field · 2026</p>
      </header>

      <section className="thanks section" id="impact">
        <div className="content-grid content-grid--thanks">
          <div className="thanks__marker" aria-hidden="true">
            <span>75</span>
            <small>Years</small>
          </div>
          <div className="thanks__message">
            <p className="eyebrow">A message of thanks</p>
            <h2>This belongs to our entire community.</h2>
            <div className="thanks__copy">
              <p>The 2026 season was an extraordinary year for Smithfield Little League.</p>
              <p>
                The generosity of our sponsors, donors, families, volunteers and community partners allowed us to make
                meaningful investments in our fields, our players and the future of our program.
              </p>
              <p>
                Every sponsorship, donated item, raffle ticket, volunteer hour and community partnership played a role.
              </p>
              <strong>We are incredibly grateful.</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="numbers section section--green" aria-labelledby="numbers-title">
        <div className="section-container">
          <div className="numbers__heading">
            <div>
              <p className="eyebrow eyebrow--gold">2026 by the numbers</p>
              <h2 id="numbers-title">A season powered by participation.</h2>
            </div>
            <p>Each number represents people choosing to invest in a better Little League experience.</p>
          </div>
          <div className="stat-grid">
            {stats.map((stat, index) => (
              <article className={`stat stat--${index + 1}`} key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="field-story section" id="fields">
        <div className="section-container">
          <SectionIntro eyebrow="Doris M. Connell Little League Field" title="Transforming Connell Field">
            <p>
              Our community&apos;s support allowed Smithfield Little League to make significant investments in Doris M.
              Connell Little League Field, improving the facility for today&apos;s players while investing in its future.
            </p>
          </SectionIntro>

          <div className="field-story__visual">
            <BeforeAfter
              fieldName="Connell Field home plate view"
              beforeSrc="/assets/impact/connell-home-before.webp"
              afterSrc="/assets/impact/connell-home-after.webp"
              beforeAlt="Connell Field viewed from behind home plate before the 2026 infield improvements"
              afterAlt="Doris M. Connell Little League Field viewed from behind home plate after the 2026 infield improvements"
            />
            <aside className="field-scope">
              <div className="status-chip"><Check aria-hidden="true" /> Completed 2026 work</div>
              <h3>A familiar field, meaningfully transformed.</h3>
              <p>
                The photographs document a rebuilt playing surface and home-plate area, a new warning track, dedicated
                field storage and the new Doris M. Connell Little League Field identity.
              </p>
              <ul>
                <li><CircleDot aria-hidden="true" /> Playing surface and infield reconstruction</li>
                <li><CircleDot aria-hidden="true" /> New warning track</li>
                <li><CircleDot aria-hidden="true" /> Field identification and landscaping</li>
                <li><CircleDot aria-hidden="true" /> Dedicated field storage</li>
              </ul>
              <p className="integrity-note">
                Move the comparison control to explore the supplied before-and-after views.
              </p>
            </aside>
          </div>

          <div className="legacy-strip">
            <div className="legacy-strip__image">
              <img
                src="/assets/doris-connell-legacy.jpg"
                alt="Historical photographs and newspaper coverage recognizing Doris M. Connell's Little League legacy"
                loading="lazy"
              />
            </div>
            <div className="legacy-strip__copy">
              <p className="eyebrow">A field with a legacy</p>
              <h3>Honoring a trailblazer while investing in the next generation.</h3>
              <p>
                The field dedication connects the league&apos;s history with the work underway today: protecting a place
                where future generations can learn, compete and belong.
              </p>
            </div>
          </div>

          <div className="field-detail-grid">
            <div className="field-detail-grid__comparison">
              <div className="field-detail-grid__heading">
                <p className="eyebrow">A new field identity</p>
                <h3>From installation to a finished place of welcome.</h3>
              </div>
              <BeforeAfter
                fieldName="Doris M. Connell Little League Field sign"
                beforeSrc="/assets/impact/connell-sign-progress.webp"
                afterSrc="/assets/impact/connell-sign-after.webp"
                beforeLabel="In progress"
                afterLabel="After"
                beforeAlt="The Doris M. Connell Little League Field sign during installation"
                afterAlt="The completed Doris M. Connell Little League Field sign and landscaping"
              />
            </div>
            <div className="field-detail-grid__stack">
              <figure className="impact-photo-card">
                <img src="/assets/impact/connell-warning-track.webp" alt="The completed warning track along the outfield at Connell Field" loading="lazy" />
                <figcaption><strong>New warning track</strong><span>Completed at Connell Field</span></figcaption>
              </figure>
              <figure className="impact-photo-card">
                <img src="/assets/impact/connell-storage.webp" alt="Completed blue storage installation at Connell Field" loading="lazy" />
                <figcaption><strong>Dedicated field storage</strong><span>Completed at Connell Field</span></figcaption>
              </figure>
            </div>
          </div>

          <div className="field-video-row">
            <div>
              <p className="eyebrow">See the completed field</p>
              <h3>A walk across Connell Field after the work.</h3>
            </div>
            <FieldVideo
              src="/assets/impact/connell-field-tour.mp4"
              poster="/assets/impact/connell-field-tour-poster.webp"
              title="Connell Field after the 2026 improvements"
              caption="Supplied 2026 field video"
            />
          </div>
        </div>
      </section>

      <section className="deerfield section section--sand">
        <div className="section-container">
          <div className="deerfield__top">
            <SectionIntro eyebrow="Deerfield Park" title="Creating better places to practice">
              <p>
                Investments at Deerfield expanded dedicated practice space and gave our players and coaches better
                facilities to develop skills throughout the season.
              </p>
            </SectionIntro>
            <div className="deerfield__badge" aria-hidden="true">
              <HardHat />
              <span>Player development</span>
            </div>
          </div>

          <PhotoPair
            leftSrc="/assets/impact/deerfield-cage-side-progress.webp"
            rightSrc="/assets/impact/deerfield-cage-side-after.webp"
            leftLabel="In progress"
            rightLabel="After"
            leftAlt="The Deerfield batting cage area while posts, gravel and drainage were being installed"
            rightAlt="The completed Deerfield batting cage with turf, netting and a hitting area"
          />

          <div className="scope-cards scope-cards--media">
            <article>
              <img src="/assets/impact/deerfield-cage-angle-after.webp" alt="Completed batting cages at Deerfield Park" loading="lazy" />
              <div className="scope-card__number">01</div>
              <h3>Dedicated batting cages</h3>
              <p>New turf, netting and defined hitting areas created a dedicated place for focused practice.</p>
            </article>
            <article>
              <img src="/assets/impact/deerfield-mound-after.webp" alt="Completed portable pitching mound at Deerfield Park" loading="lazy" />
              <div className="scope-card__number">02</div>
              <h3>Pitching mounds</h3>
              <p>Completed pitching resources expanded the ways players and coaches can work during the season.</p>
            </article>
            <article>
              <img src="/assets/impact/deerfield-cage-home-after.webp" alt="Home plate and hitting lane inside the completed Deerfield batting cage" loading="lazy" />
              <div className="scope-card__number">03</div>
              <h3>Purpose-built practice space</h3>
              <p>Clear hitting lanes and a finished home-plate area make the space ready for player development.</p>
            </article>
          </div>

          <div className="deerfield-media-row">
            <figure className="deerfield-entrance-photo">
              <img src="/assets/impact/deerfield-entrance-after.webp" alt="Landscaped entrance area at Deerfield Park after the 2026 work" loading="lazy" />
              <figcaption><strong>Deerfield Park</strong><span>A completed player-development setting</span></figcaption>
            </figure>
            <FieldVideo
              src="/assets/impact/deerfield-cage-tour.mp4"
              poster="/assets/impact/deerfield-cage-tour-poster.webp"
              title="Inside the completed Deerfield batting cages"
              caption="Supplied 2026 practice-facility video"
            />
          </div>
        </div>
      </section>

      <section className="beyond section">
        <div className="section-container">
          <SectionIntro eyebrow="Beyond the fields" title="Investing in the player experience">
            <p>
              Support didn&apos;t simply improve facilities. It strengthened the details that shape every practice, game
              and visit to a Smithfield Little League field.
            </p>
          </SectionIntro>
          <div className="investment-grid">
            <article>
              <img src="/assets/impact/whipple-flags.webp" alt="American, Rhode Island and Smithfield Little League flags flying at Whipple Field" loading="lazy" />
              <div className="investment-card__copy"><span>Whipple Field</span><h3>Visible community pride</h3><p>New flags added a meaningful sense of place at the field.</p></div>
            </article>
            <article>
              <img src="/assets/impact/connell-storage.webp" alt="Completed field storage installation at Connell Field" loading="lazy" />
              <div className="investment-card__copy"><span>Connell Field</span><h3>On-site field storage</h3><p>A completed storage installation added practical capacity at the facility.</p></div>
            </article>
            <article>
              <img src="/assets/impact/deerfield-mound-after.webp" alt="Pitching mound available for player-development work at Deerfield Park" loading="lazy" />
              <div className="investment-card__copy"><span>Deerfield Park</span><h3>Player-development resources</h3><p>A completed pitching mound gave players and coaches another way to work.</p></div>
            </article>
          </div>
        </div>
      </section>

      <section className="support-system section section--green">
        <div className="section-container">
          <SectionIntro eyebrow="How our community made it possible" title="Many forms of support. One shared impact." light>
            <p>Thousands of individual acts of support combined to create something much bigger.</p>
          </SectionIntro>
          <div className="support-grid">
            {supportWays.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <div className="support-card__icon"><Icon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sponsors section" id="sponsors">
        <div className="section-container">
          <SectionIntro eyebrow="54 community partners" title="Thank you to our 2026 sponsors">
            <p>
              Our sponsors make it possible for Smithfield Little League to invest beyond registration fees and create a
              better experience for our players and families.
            </p>
          </SectionIntro>
          {!dataReady ? (
            <div className="loading-card" aria-live="polite">Loading the 2026 sponsor recognition...</div>
          ) : sponsors.length ? (
            <SponsorSection sponsors={sponsors} />
          ) : (
            <div className="loading-card">Sponsor data could not be loaded.</div>
          )}
        </div>
      </section>

      <section className="community section section--sand" id="community">
        <div className="section-container">
          <SectionIntro eyebrow="Community supporter directory" title="Thank you to our community">
            <p>
              More than businesses and formal sponsorships made this year possible. Thousands of families, friends and
              members of our community supported Smithfield Little League through raffles, events, donations and other
              contributions.
            </p>
          </SectionIntro>
          <SupporterDirectory supporters={supporters} />
        </div>
      </section>

      <section className="contributors section">
        <div className="section-container">
          <SectionIntro eyebrow="Additional recognition" title="Fundraising & in-kind contributors">
            <p>
              Formal sponsorships are recognized separately from donated prizes, services, materials and labor so every
              contribution is represented accurately.
            </p>
          </SectionIntro>
          <div className="contributor-grid">
            <article>
              <div className="contributor-card__top">
                <Gift aria-hidden="true" />
                <span>Fundraising contributors</span>
              </div>
              <h3>Raffle prizes, donated items and direct support</h3>
              <p>Recognized separately from formal sponsorship packages.</p>
              <div className="recognition-name-grid">
                {contributors.fundraising.map((contributor) => (
                  <div key={`${contributor.name}-${contributor.contribution}`}>
                    {contributor.logo && (
                      <div className={`recognition-logo${contributor.dark ? " recognition-logo--dark" : ""}`}>
                        <img src={contributor.logo} alt="" loading="lazy" />
                      </div>
                    )}
                    <div className="recognition-name__copy">
                      <strong>{contributor.name}</strong>
                      <span>{contributor.contribution}</span>
                    </div>
                  </div>
                ))}
              </div>
            </article>
            <article>
              <div className="contributor-card__top">
                <Wrench aria-hidden="true" />
                <span>In-kind partners</span>
              </div>
              <h3>Materials, labor, services and expertise</h3>
              <p>Recognized without implying a formal paid sponsorship.</p>
              <div className="recognition-name-grid recognition-name-grid--single">
                {contributors.inKind.map((contributor) => (
                  <div key={contributor.name}>
                    {contributor.logo && (
                      <div className="recognition-logo">
                        <img src={contributor.logo} alt="" loading="lazy" />
                      </div>
                    )}
                    <div className="recognition-name__copy">
                      <strong>{contributor.name}</strong>
                      <span>{contributor.contribution}</span>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="partners section section--cream">
        <div className="section-container partners__layout">
          <div className="partners__story">
            <p className="eyebrow">Town & community partners</p>
            <h2>A community effort</h2>
            <p className="partners__lead">
              Successful field and facility improvements require more than fundraising. They also require cooperation,
              expertise, labor and partnership.
            </p>
            <p className="integrity-note">
              Recognition here reflects partnership and does not imply a financial contribution.
            </p>
            <figure className="partner-award">
              <img src="/assets/impact/dpw-award.webp" alt="An award recognizing the Town of Smithfield Department of Public Works" loading="lazy" />
              <figcaption>Smithfield Department of Public Works recognition</figcaption>
            </figure>
          </div>
          <div className="partner-list">
            <article>
              <Landmark aria-hidden="true" />
              <div><strong>Town of Smithfield</strong><span>Community partner</span></div>
            </article>
            <article>
              <Building2 aria-hidden="true" />
              <div><strong>Smithfield Department of Public Works</strong><span>Facility and project partner</span></div>
            </article>
          </div>
        </div>
      </section>

      <section className="volunteers section section--green">
        <div className="section-container volunteers__layout">
          <div className="volunteers__copy">
            <div className="volunteers__icon"><HandHeart aria-hidden="true" /></div>
            <p className="eyebrow eyebrow--gold">Powered by volunteers</p>
            <h2>Behind every Little League memory are people who gave their time.</h2>
            <p>
              Behind every game, fundraiser, field improvement and Little League memory are volunteers giving their time
              to make it happen.
            </p>
          </div>
          <div className="volunteer-list">
            {volunteerGroups.map((group, index) => (
              <div key={group}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{group}</strong>
                <Check aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="future section section--sand">
        <div className="section-container future__layout">
          <div className="future__photo">
            <img
              src="/assets/doris-connell-legacy.jpg"
              alt="Historical Smithfield Little League photographs and newspaper clippings"
              loading="lazy"
            />
            <span>75 years of history</span>
          </div>
          <div className="future__copy">
            <p className="eyebrow">Building for the next 75 years</p>
            <h2>Protecting what matters. Preparing for what comes next.</h2>
            <p>
              Smithfield Little League will continue investing thoughtfully in its facilities, player development and
              the Little League experience.
            </p>
            <div className="whats-next">
              <div className="whats-next__title"><span>What&apos;s next</span><small>Future plans · not completed work</small></div>
              <ul>
                {nextPriorities.map((priority) => <li key={priority}><ChevronRight aria-hidden="true" />{priority}</li>)}
              </ul>
            </div>
            <strong className="future__closing">Thank you for helping us build the foundation for what&apos;s next.</strong>
          </div>
        </div>
      </section>

      <section className="final-thanks" id="thank-you">
        <div className="final-thanks__glow" aria-hidden="true" />
        <img src="/assets/sll-75th-logo.webp" alt="Smithfield Little League 75th anniversary" />
        <p className="eyebrow eyebrow--gold">From all of us at Smithfield Little League</p>
        <h2>Thank you, Smithfield.</h2>
        <p>
          To every business, family, donor, volunteer, town partner and community member who supported Smithfield Little
          League in 2026: this belongs to you too.
        </p>
        <div className="final-thanks__line" />
        <strong>75 Years of Little League.</strong>
        <span>One incredible community.</span>
        <a href="#top">Back to the beginning <ArrowDown aria-hidden="true" /></a>
      </section>

      
    </main>
  );
}

